import { HStack, Image, Select, Text, VStack } from 'native-base';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { View } from 'react-native';
import { PaymentComponentType } from '../types/types';
import { useConfig } from '../../Common/hooks/useConfig';

export const TillComponent = React.forwardRef((props: PaymentComponentType, ref) => {
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');

  const [validCard, setValidCard] = useState(false);
  const [validCCV, setValidCCV] = useState(false);
  const [cardHolderName, setCardHolderName] = useState('');

  // @ts-ignore
  const payment = useRef(new PaymentJs());

  const { configForBrand } = useConfig();

  const config = configForBrand(props.brand);

  const cardHolderNameValid = cardHolderName?.length >= 1;

  const [clicked, setClicked] = useState(false);

  useImperativeHandle(ref, () => ({
    confirmPayment: () => {
      const promise = new Promise((accept, reject) => {
        setClicked(true);

        if (!cardHolderNameValid) {
          reject();
          return;
        }

        try {
          // @ts-ignore
          payment.current.tokenize(
            {
              card_holder: cardHolderName,
              month: expMonth,
              year: expYear,
            },
            (token: string, cardData: any) => {
              const payload = JSON.stringify({
                type: 'newcard',
                gateway: 'till',
                card: token,
                expMonth,
                expYear,
                lastFour: cardData.last_four_digits,
                cardType: cardData.card_type,
              });

              Sentry.addBreadcrumb({
                category: 'consult',
                message: 'Encrypted payment values',
                level: 'info',
              });

              accept(payload);
            },
            (errors) => {
              console.error(errors);
              reject(errors);
            },
          );
        } catch (e) {
          console.error(e);

          reject(e);
        }
      });

      return promise;
    },
  }));

  useEffect(() => {
    props.setCardCompleted(validCCV && validCard);
  }, [validCard, validCCV]);

  useEffect(() => {
    if (config?.paymentGatewayAPIPublicKey) {
      // @ts-ignore
      payment.current.init(
        config?.paymentGatewayAPIPublicKey,
        'number_div',
        'ccv_div',
        function (paymentObj) {
          paymentObj.enableAutofill();

          paymentObj.onAutofill(function (data: any) {
            setExpMonth(data.month);
            setExpYear(data.year);
          });

          paymentObj.numberOn('input', function (data: any) {
            setValidCard(data.validNumber);
          });

          paymentObj.cvvOn('input', function (data: any) {
            setValidCCV(data.validCvv);
          });

          const style = {
            border: '1px solid rgb(212, 212, 212)',
            height: 32,
            borderRadius: 4,
            paddingLeft: 10,
            fontSize: 16,
          };

          paymentObj.setNumberStyle(style);
          paymentObj.setCvvStyle(style);
        },
      );
    }
  }, []);

  return (
    <VStack space={1} opacity={props.disabled ? 0.5 : 1}>
      <HStack space={2} alignItems={'center'}>
        <Text color={'paymentBoxTitle'} fontWeight={'600'} flex={1}>
          Your Payment Card
        </Text>
        <Image
          borderRadius={'sm'}
          h={8}
          w={10}
          source={require('../../Assets/PaymentMethods/1.png')}
        />
        <Image
          borderRadius={'sm'}
          h={8}
          w={10}
          source={require('../../Assets/PaymentMethods/2.png')}
        />
        <Image
          borderRadius={'sm'}
          h={8}
          w={10}
          source={require('../../Assets/PaymentMethods/22.png')}
        />
      </HStack>

      <VStack space={4}>
        <VStack
          bg={clicked && !cardHolderNameValid ? 'red.200' : null}
          p={clicked && !cardHolderNameValid ? 2 : 0}
          borderRadius={'sm'}
        >
          <Text fontWeight={600}>Card Holder Name</Text>

          <input
            style={{
              border: '1px solid rgb(212, 212, 212)',
              height: '32px',
              'border-radius': '4px',
              'font-size': '16px',
            }}
            type={'text'}
            value={cardHolderName}
            onChange={(e) => setCardHolderName(e.target.value)}
          />
        </VStack>

        <VStack>
          <Text fontWeight={600}>Card Number</Text>
          <View nativeID={'number_div'} style={{ height: 36 }} />
        </VStack>

        <HStack space={4}>
          <VStack style={{ width: 200 }}>
            <Text fontWeight={600}>Expiry</Text>

            <HStack space={2}>
              <Select
                flex={1}
                bg={'white'}
                placeholder={'MM'}
                selectedValue={expMonth}
                style={{ height: 33 }}
                onValueChange={(e) => setExpMonth(e)}
              >
                {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map(
                  (number) => (
                    <Select.Item label={number.toString()} value={number.toString()} />
                  ),
                )}
              </Select>

              <Select
                flex={1}
                bg={'white'}
                placeholder={'YYYY'}
                selectedValue={expYear}
                style={{ height: 33 }}
                onValueChange={(e) => setExpYear(e)}
              >
                {[2024, 2025, 2026, 2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033, 2034].map(
                  (number) => (
                    <Select.Item label={number.toString()} value={number.toString()} />
                  ),
                )}
              </Select>
            </HStack>
          </VStack>

          <VStack w={20}>
            <Text fontWeight={600}>CCV</Text>
            <View nativeID={'ccv_div'} style={{ height: 36 }} />
          </VStack>
        </HStack>
      </VStack>
    </VStack>
  );
});
